import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  AppBar,
  Toolbar,
  Tooltip,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import { Chip } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { green, blue } from '@mui/material/colors';
import dayjs from 'dayjs';
import MenuIcon from '@mui/icons-material/Menu';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import AddTaskIcon from '@mui/icons-material/AddTask';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FormatListBulletedAddIcon from './icons/FormatListBulletedAddIcon';
// import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
// import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import { ImportLogsDialog } from './ImportLogsDialog';
import ConfirmationDialog from './ConfirmationDialog'; // Import the new component
import ProjectLogsTableDialog from './ProjectLogsTableDialog';
import ProjectLogsTable from './ProjectLogsTable';
import UsersTableDialog from './UsersTableDialog';
import {
  enablePushNotifications,
  getNotificationPermission,
  hasExistingPushSubscription,
  isNotificationCapable,
  sendTestNotification,
  syncExistingSubscription,
} from './notifications';
import { usePwaInstaller } from './usePwaInstaller';

function App() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
  const SSE_BASE_URL = process.env.REACT_APP_SSE_BASE_URL || 'http://localhost:3001/events';
  const isLocalhost =
    typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const allowSelfRegistration =
    String(process.env.REACT_APP_ALLOW_SELF_REGISTRATION || '').toLowerCase() === 'true' || isLocalhost;
  const [serverUrl, setServerUrl] = useState(API_BASE_URL);
  const [sseUrl, setSseUrl] = useState(SSE_BASE_URL);
  const [projects, setProjects] = useState([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [projectActivities, setProjectActivities] = useState({});

  // For manual log form
  const [showAddLogProjectId, setShowAddLogProjectId] = useState(null);
  const [manualStartDate, setManualStartDate] = useState(dayjs());
  const [manualEndDate, setManualEndDate] = useState(dayjs());
  const [showAddDurationProjectId, setShowAddDurationProjectId] = useState(null);
  const [manualDurationInput, setManualDurationInput] = useState('');

  // For dynamic time updates
  const [now, setNow] = useState(Date.now());

  // For deleting a project from a dropdown
  const [deleteProjectId, setDeleteProjectId] = useState('');
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  // Media query for responsive design
  const isMobile = useMediaQuery('(max-width: 768px)'); // Adjust breakpoint as needed

  // Authentication states
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loggedInUsername, setLoggedInUsername] = useState(null);
  const [loggedInRole, setLoggedInRole] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(!!token); // Check if token exists
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProjectId, setImportProjectId] = useState(null);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  // Change Password dialog state
  const [openChangePasswordDialog, setOpenChangePasswordDialog] = useState(false);
  const [currentPasswordInput, setCurrentPasswordInput] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'error', 'warning', 'info', 'success'

  // For logs table dialog
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [projectLogs, setProjectLogs] = useState([]);
  const [selectedProjectForLogs, setSelectedProjectForLogs] = useState(null);

  // Description editing state
  const [editingDescriptionProjectId, setEditingDescriptionProjectId] = useState(null);
  const [descriptionInput, setDescriptionInput] = useState('');

  // Budget editing state
  const [editingBudgetProjectId, setEditingBudgetProjectId] = useState(null);
  const [budgetInput, setBudgetInput] = useState('');
  const [showProjectCards, setShowProjectCards] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    const storedValue = window.localStorage.getItem('showProjectCards');
    return storedValue === null ? true : storedValue === 'true';
  });

  // PWA + notifications
  const notificationSupported = isNotificationCapable();
  const [notificationPermission, setNotificationPermission] = useState(getNotificationPermission());
  const [hasPushSubscription, setHasPushSubscription] = useState(false);
  const [isNotificationBusy, setIsNotificationBusy] = useState(false);
  const {
    canInstall: canInstallPwa,
    install: installPwa,
    isInstalling: isInstallingPwa,
    isInstalled: isPwaInstalled,
  } = usePwaInstaller();

  const handleInstallApplication = async () => {
    const outcome = await installPwa();
    if (outcome === 'accepted') {
      showSnackbar('Application installed! Find it in your apps list.', 'success');
      return;
    }
    if (outcome === 'dismissed') {
      showSnackbar('Install dismissed. You can try again later from this button.', 'info');
      return;
    }
    showSnackbar('Install prompt is not ready yet. Use your browser menu to add it manually.', 'info');
  };

  useEffect(() => {
    const currentUrl = window.location.origin;
    // In dev (e.g., :3000), keep default API base (likely :3001). In prod/same-origin, switch to relative.
    const isDevHost = /:(3000|5173)/.test(currentUrl);
    if (!isDevHost) {
      setServerUrl(`${currentUrl}/api`);
      setSseUrl(`${currentUrl}/events`);
    }

    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Public project route detection: by stable publicId (/projects/p/:publicId), by id (/projects/id/:id), or by name (/projects/:name)
  const [publicProjectName, setPublicProjectName] = useState(null);
  const [publicProjectId, setPublicProjectId] = useState(null);
  const [publicProjectPublicId, setPublicProjectPublicId] = useState(null);
  useEffect(() => {
    const path = window.location.pathname;
    // Prefer ID-based route
    const idMatch = path.match(/^\/(project|projects)\/id\/(\d+)$/);
    if (idMatch && idMatch[2]) {
      setPublicProjectId(idMatch[2]);
      setPublicProjectName(null);
      setPublicProjectPublicId(null);
      return;
    }
    // PublicId-based route
    const pubIdMatch = path.match(/^\/(project|projects)\/p\/([a-zA-Z0-9]{8})$/);
    if (pubIdMatch && pubIdMatch[2]) {
      setPublicProjectPublicId(pubIdMatch[2]);
      setPublicProjectId(null);
      setPublicProjectName(null);
      return;
    }
    const nameMatch = path.match(/^\/(project|projects)\/(.+)$/);
    if (nameMatch && nameMatch[2]) {
      setPublicProjectName(decodeURIComponent(nameMatch[2]));
      setPublicProjectId(null);
      setPublicProjectPublicId(null);
      return;
    }
    setPublicProjectName(null);
    setPublicProjectId(null);
    setPublicProjectPublicId(null);
  }, []);

  useEffect(() => {
    if (!notificationSupported) {
      setHasPushSubscription(false);
      return;
    }
    hasExistingPushSubscription()
      .then((subscription) => setHasPushSubscription(Boolean(subscription)))
      .catch((err) => console.error('Failed to check push subscription', err));
  }, [notificationSupported]);

  useEffect(() => {
    if (!notificationSupported || !token) {
      return;
    }
    syncExistingSubscription(token)
      .then((synced) => {
        if (synced) {
          setHasPushSubscription(true);
          setNotificationPermission('granted');
        }
      })
      .catch((err) => console.error('Failed to sync push subscription', err));
  }, [notificationSupported, token]);

  const [publicProject, setPublicProject] = useState(null);
  const [publicProjectLogs, setPublicProjectLogs] = useState([]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('showProjectCards', showProjectCards ? 'true' : 'false');
    }
  }, [showProjectCards]);

  const fetchPublicProjectData = async (name) => {
    try {
      const projRes = await axios.get(`${serverUrl}/public/projects/${encodeURIComponent(name)}`);
      setPublicProject(projRes.data);
      const logsRes = await axios.get(`${serverUrl}/public/projects/${encodeURIComponent(name)}/logs`);
      setPublicProjectLogs(logsRes.data);
    } catch (e) {
      console.error('Failed to load public project data', e);
      setPublicProject(null);
      setPublicProjectLogs([]);
      showSnackbar('Public project not found', 'error');
    }
  };

  const fetchPublicProjectDataById = async (id) => {
    try {
      const projRes = await axios.get(`${serverUrl}/public/projects/id/${encodeURIComponent(id)}`);
      setPublicProject(projRes.data);
      const logsRes = await axios.get(`${serverUrl}/public/projects/id/${encodeURIComponent(id)}/logs`);
      setPublicProjectLogs(logsRes.data);
    } catch (e) {
      console.error('Failed to load public project data by id', e);
      setPublicProject(null);
      setPublicProjectLogs([]);
      showSnackbar('Public project not found', 'error');
    }
  };

  const fetchPublicProjectDataByPublicId = async (publicId) => {
    try {
      const projRes = await axios.get(`${serverUrl}/public/projects/p/${encodeURIComponent(publicId)}`);
      setPublicProject(projRes.data);
      const logsRes = await axios.get(`${serverUrl}/public/projects/p/${encodeURIComponent(publicId)}/logs`);
      setPublicProjectLogs(logsRes.data);
    } catch (e) {
      console.error('Failed to load public project data by publicId', e);
      setPublicProject(null);
      setPublicProjectLogs([]);
      showSnackbar('Public project not found', 'error');
    }
  };

  useEffect(() => {
    if (!serverUrl) return;
    if (publicProjectPublicId) {
      fetchPublicProjectDataByPublicId(publicProjectPublicId);
    } else if (publicProjectId) {
      fetchPublicProjectDataById(publicProjectId);
    } else if (publicProjectName) {
      fetchPublicProjectData(publicProjectName);
    }
  }, [publicProjectPublicId, publicProjectId, publicProjectName, serverUrl]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${serverUrl}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects(res.data);
      const activities = {};
      res.data.forEach(proj => {
        activities[proj.id] = proj.activity || '';
      });
      setProjectActivities(activities);
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 403) {
        logout();
      }
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchProjects();
    }
  }, [serverUrl, isLoggedIn]);

  // SSE Connection
  useEffect(() => {
    let eventSource;
    if (isLoggedIn) {
      eventSource = new EventSource(sseUrl);

      eventSource.onopen = () => {
        console.log('SSE connection opened');
      };

      eventSource.onerror = (error) => {
        console.error('SSE error:', error);
        showSnackbar('SSE connection error', 'error');
      };

      eventSource.addEventListener('project-created', (event) => {
        const newProject = JSON.parse(event.data);
        console.log('Project created:', newProject);
        fetchProjects();
        showSnackbar(`Project ${newProject.name} created`, 'success');
      });

      eventSource.addEventListener('project-updated', (event) => {
        const updatedProject = JSON.parse(event.data);
        console.log('Project updated:', updatedProject);
        fetchProjects();
        showSnackbar(`Project ${updatedProject.name} updated`, 'success');
      });

      eventSource.addEventListener('project-deleted', (event) => {
        const deletedProject = JSON.parse(event.data);
        console.log('Project deleted:', deletedProject);
        fetchProjects();
        showSnackbar(`Project ${deletedProject.name} deleted`, 'success');
      });

      eventSource.addEventListener('project-started', (event) => {
        const startedProject = JSON.parse(event.data);
        console.log('Project started:', startedProject);
        fetchProjects();
        showSnackbar(`Project ${startedProject.name} started`, 'success');
      });

      eventSource.addEventListener('project-stopped', (event) => {
        const stoppedProject = JSON.parse(event.data);
        console.log('Project stopped:', stoppedProject);
        fetchProjects();
        showSnackbar(`Project ${stoppedProject.name} stopped`, 'success');
      });
    }

    return () => {
      if (eventSource) {
        eventSource.close();
        console.log('SSE connection closed');
      }
    };
  }, [isLoggedIn, serverUrl, sseUrl]);

  // Create
  const createProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      await axios.post(
        `${serverUrl}/projects`,
        {
          name: newProjectName,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNewProjectName('');
      fetchProjects(); // Should be Removed, now handled by SSE, but doesn't seem to work in all setups..
      showSnackbar('Project created successfully', 'success'); // Removed, now handled by SSE
    } catch (err) {
      console.error(err);
      const reason = err.response?.data?.error || err.message || 'Unknown error';
      showSnackbar(`Error creating project: ${reason}`, 'error');
    }
  };

  const startEditDescription = (proj) => {
    setEditingDescriptionProjectId(proj.id);
    setDescriptionInput(proj.description ?? '');
  };

  const cancelEditDescription = () => {
    setEditingDescriptionProjectId(null);
    setDescriptionInput('');
  };

  const saveDescription = async (projectId) => {
    try {
      await axios.put(
        `${serverUrl}/projects/${projectId}/description`,
        { description: descriptionInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar('Description saved', 'success');
      cancelEditDescription();
      fetchProjects();
    } catch (err) {
      console.error(err);
      showSnackbar('Error saving description', 'error');
    }
  };

  const startEditBudget = (proj) => {
    setEditingBudgetProjectId(proj.id);
    setBudgetInput(
      proj.budget === null || proj.budget === undefined ? '' : String(proj.budget)
    );
  };

  const cancelEditBudget = () => {
    setEditingBudgetProjectId(null);
    setBudgetInput('');
  };

  const saveBudget = async (projectId) => {
    try {
      let payloadBudget = null;
      if (budgetInput !== null && budgetInput !== undefined && String(budgetInput).trim() !== '') {
        const parsed = parseFloat(budgetInput);
        if (Number.isNaN(parsed)) {
          showSnackbar('Invalid budget value', 'error');
          return;
        }
        payloadBudget = parsed;
      }

      await axios.put(
        `${serverUrl}/projects/${projectId}/budget`,
        { budget: payloadBudget },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      cancelEditBudget();
      fetchProjects();
      showSnackbar('Budget saved', 'success');
    } catch (err) {
      console.error(err);
      showSnackbar('Error saving budget', 'error');
    }
  };

  // Start
  const startProject = async (id) => {
    try {
      await axios.post(`${serverUrl}/projects/${id}/start`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchProjects(); // Removed, now handled by SSE
      showSnackbar('Project started', 'success'); // Removed, now handled by SSE
    } catch (err) {
      console.error(err);
      showSnackbar('Error starting project', 'error');
    }
  };

  // Stop
  const stopProject = async (id) => {
    try {
      await axios.post(`${serverUrl}/projects/${id}/stop`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchProjects(); // Removed, now handled by SSE
      showSnackbar('Project stopped', 'success'); // Removed, now handled by SSE
    } catch (err) {
      console.error(err);
      showSnackbar('Error stopping project', 'error');
    }
  };

  // Add log
  const handleAddLogClick = (projectId) => {
    setShowAddDurationProjectId(null);
    if (showAddLogProjectId === projectId) {
      setShowAddLogProjectId(null);
    } else {
      setShowAddLogProjectId(projectId);
    }
    setManualStartDate(dayjs());
    setManualEndDate(dayjs());
  };

  const handleUpdateLastEndTimeClick = async (projectId) => {
    try {
      const response = await axios.get(`${serverUrl}/projects/${projectId}/lastLog`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const lastLog = response.data;
      if (lastLog) {
        setManualStartDate(dayjs(lastLog.startTime));
        setManualEndDate(dayjs(lastLog.endTime));
      } else {
        showSnackbar('No previous logs found for this project.', 'info');
      }
    } catch (error) {
      console.error('Error fetching last log:', error);
      showSnackbar('Failed to fetch last log.', 'error');
    }
    if (showAddLogProjectId === projectId) {
      setShowAddLogProjectId(null);
    } else {
      setShowAddLogProjectId(projectId);
    }
  };

  const handleAddDurationClick = (projectId) => {
    setShowAddLogProjectId(null);
    if (showAddDurationProjectId === projectId) {
      setShowAddDurationProjectId(null);
      setManualDurationInput('');
    } else {
      setShowAddDurationProjectId(projectId);
      setManualDurationInput('');
    }
  };

  const handleShowTableClick = async (projectId) => {
    try {
      const response = await axios.get(`${serverUrl}/projects/${projectId}/logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjectLogs(response.data);
      const proj = projects.find(p => p.id === projectId);
      setSelectedProjectForLogs(proj || null);
      setLogsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching project logs:', error);
      showSnackbar('Failed to fetch project logs.', 'error');
    }
  };

  const submitManualLog = async (projectId) => {
    if (!manualStartDate || !manualEndDate) {
      showSnackbar('Please select both start and end times.', 'warning');
      return;
    }
    if (manualEndDate.isBefore(manualStartDate)) {
      showSnackbar('End time cannot be before start time.', 'warning');
      return;
    }

    try {
      if (showAddLogProjectId === projectId) {
        // Update the last log record if initiated from Update last time
        await axios.put(
          `${serverUrl}/projects/${projectId}/lastLog`,
          {
            startTime: manualStartDate.toISOString(),
            endTime: manualEndDate.toISOString(),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        showSnackbar('Last log updated successfully', 'success');
      } else {
        // Add a new log record
        await axios.post(
          `${serverUrl}/projects/${projectId}/addLog`,
          {
            startTime: manualStartDate.toISOString(),
            endTime: manualEndDate.toISOString(),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        showSnackbar('Manual log added successfully', 'success');
      }

      setShowAddLogProjectId(null);
      fetchProjects();
    } catch (err) {
      console.error(err);
      showSnackbar('Error saving log', 'error');
    }
  };

  const submitManualDurationLog = async (projectId) => {
    const raw = manualDurationInput.trim();
    if (!raw) {
      showSnackbar('Please enter a duration in hours.', 'warning');
      return;
    }
    const normalized = raw.replace(',', '.');
    const parsed = parseFloat(normalized);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showSnackbar('Duration must be a positive number.', 'warning');
      return;
    }
    try {
      await axios.post(
        `${serverUrl}/projects/${projectId}/addDurationLog`,
        {
          durationHours: parsed,
          activity: projectActivities[projectId] || '',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setShowAddDurationProjectId(null);
      setManualDurationInput('');
      fetchProjects();
      showSnackbar('Duration log added successfully', 'success');
    } catch (err) {
      console.error(err);
      const reason = err.response?.data?.error || err.message || 'Error saving duration log';
      showSnackbar(`Error saving duration log: ${reason}`, 'error');
    }
  };

  const cancelManualDuration = () => {
    setShowAddDurationProjectId(null);
    setManualDurationInput('');
  };

  // Export logs
  const exportLogs = async (proj) => {
    try {
      const { id, name } = proj;

      const res = await axios.get(`${serverUrl}/projects/${id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob', // Important for handling binary data
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project-${name}-logs.csv`); // Or whatever filename you want
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar('Logs exported successfully', 'success');
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 403) {
        logout();
      } else {
        // Enhanced error handling to show more details
        const errorMessage = `Error exporting logs: ${err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : err.message
          } ${err.response && err.response.data
            ? `\nResponse data: ${JSON.stringify(err.response.data)}`
            : ''
          }`;
        showSnackbar(errorMessage, 'error');
      }
    }
  };


  const importLogs = async (id) => {
    setImportProjectId(id);
    setIsImporting(true);
  };

  const closeImportDialog = () => {
    setIsImporting(false);
    setImportProjectId(null);
  };

  // Delete from dropdown
  const handleDeleteProject = async () => {
    try {
      await axios.delete(`${serverUrl}/projects/${deleteProjectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteProjectId('');
      fetchProjects(); // Removed, now handled by SSE
      showSnackbar('Project deleted successfully', 'success'); // Removed, now handled by SSE
    } catch (err) {
      console.error(err);
      showSnackbar('Error deleting project', 'error');
    }
  };

  const deleteSelectedProject = () => {
    if (!deleteProjectId) {
      showSnackbar('Please select a project to delete.', 'warning');
      return;
    }
    setIsConfirmDialogOpen(true); // Open the confirmation dialog
  };

  const updateActivity = async (projectId, newActivity) => {
    try {
      await axios.put(
        `${serverUrl}/projects/${projectId}/activity`,
        {
          activity: newActivity,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProjectActivities((prevActivities) => ({
        ...prevActivities,
        [projectId]: newActivity,
      }));
      fetchProjects(); // No need to fetch all projects, just update the state
      showSnackbar('Activity updated successfully', 'success');
    } catch (err) {
      console.error(err);
      showSnackbar('Error updating activity', 'error');
    }
  };

  // Helpers for time display
  function parseMsToDDHHMMSS(ms) {
    if (ms < 0) ms = 0;
    let totalSeconds = Math.floor(ms / 1000);

    const days = Math.floor(totalSeconds / 86400);
    totalSeconds %= 86400;

    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return { days, hours, minutes, seconds };
  }

  function formatDDHHMMSS(ms, isActive) {
    const { days, hours, minutes, seconds } = parseMsToDDHHMMSS(ms);
    const ddStr = String(days).padStart(2, '0');
    const hhStr = String(hours).padStart(2, '0');
    const mmStr = String(minutes).padStart(2, '0');
    const ssStr = String(seconds).padStart(2, '0');

    if (isActive) {
      return `${ddStr} ${hhStr}:${mmStr}:${ssStr}`;
    } else {
      return `${ddStr} ${hhStr}:${mmStr}`;
    }
  }

  function getDisplayTime(proj) {
    let baseMs = proj.totalHours * 3600000;
    if (proj.isActive && proj.lastStartTime) {
      const lastStartMs = new Date(proj.lastStartTime).getTime();
      const diffMs = now - lastStartMs;
      return baseMs + diffMs;
    } else {
      return baseMs;
    }
  }

  // Authentication functions
  const handleLogin = async () => {
    try {
      const res = await axios.post(`${serverUrl}/login`, {
        username,
        password,
      });
      const { token } = res.data;
      setToken(token);
      localStorage.setItem('token', token);
      setIsLoggedIn(true);
      // Decode username from JWT and store
      const decoded = decodeJwt(token);
      setLoggedInUsername(decoded && decoded.username ? decoded.username : username);
      setLoggedInRole(decoded && decoded.role ? decoded.role : null);
      setOpenLoginDialog(false);
      showSnackbar('Login successful', 'success');
    } catch (err) {
      console.error('Login failed:', err.response ? err.response.data : err.message);
      const errorMessage = `Login failed: ${err.response && err.response.data && err.response.data.message
        ? err.response.data.message
        : err.message
        } ${err.response && err.response.data
          ? `\nResponse data: ${JSON.stringify(err.response.data)}`
          : ''
        }`;
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleRegister = async () => {
    if (!allowSelfRegistration) {
      showSnackbar('Self-registration is disabled for this deployment.', 'info');
      return;
    }
    try {
      await axios.post(`${serverUrl}/register`, {
        username,
        password,
      });
      showSnackbar('Registration successful! Please log in.', 'success');
      setOpenRegisterDialog(false);
      setOpenLoginDialog(true);
    } catch (err) {
      console.error('Registration failed:', err.response ? err.response.data : err.message);
      const errorMessage = `Registration failed: ${err.response && err.response.data && err.response.data.message
        ? err.response.data.message
        : err.message
        } ${err.response && err.response.data
          ? `\nResponse data: ${JSON.stringify(err.response.data)}`
          : ''
        }`;
      showSnackbar(errorMessage, 'error');
    }
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    setLoggedInUsername(null);
    setLoggedInRole(null);
    setProjects([]);
    showSnackbar('Logged out', 'success');
  };

  const handleOpenLoginDialog = () => {
    setOpenLoginDialog(true);
  };

  // Decode JWT helper
  function decodeJwt(tkn) {
    try {
      const base64Url = tkn.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const jsonPayload = decodeURIComponent(
        atob(padded)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  }

  // Initialize username from existing token on load
  useEffect(() => {
    if (token) {
      const decoded = decodeJwt(token);
      setLoggedInUsername(decoded && decoded.username ? decoded.username : null);
      setLoggedInRole(decoded && decoded.role ? decoded.role : null);
    } else {
      setLoggedInUsername(null);
      setLoggedInRole(null);
    }
  }, [token]);

  const handleCloseLoginDialog = () => {
    setOpenLoginDialog(false);
  };

  const handleCloseRegisterDialog = () => {
    setOpenRegisterDialog(false);
  };

  // Snackbar functions
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleError = (message) => {
    showSnackbar(message, 'error');
  };

  const handleMessage = (message) => {
    showSnackbar(message, 'success');
  };

  const isNotificationReady = notificationPermission === 'granted' && hasPushSubscription;

  const handleEnableNotifications = async () => {
    if (!notificationSupported) {
      showSnackbar('Notifications are not supported in this browser.', 'warning');
      return;
    }
    setIsNotificationBusy(true);
    try {
      await enablePushNotifications(token);
      setNotificationPermission('granted');
      setHasPushSubscription(true);
      showSnackbar('Notifications enabled. You will receive alerts even when the app is closed.', 'success');
    } catch (err) {
      console.error('Failed to enable notifications', err);
      setNotificationPermission(getNotificationPermission());
      showSnackbar(err.message || 'Unable to enable notifications', 'error');
    } finally {
      setIsNotificationBusy(false);
    }
  };

  const handleSendTestNotification = async () => {
    if (!token) {
      showSnackbar('Login to send yourself a push notification.', 'info');
      return;
    }
    setIsNotificationBusy(true);
    try {
      await sendTestNotification(token);
      showSnackbar('Test notification sent! Check your device tray.', 'success');
    } catch (err) {
      console.error('Failed to send test notification', err);
      showSnackbar(err.message || 'Unable to send a test notification', 'error');
    } finally {
      setIsNotificationBusy(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      await axios.post(`${serverUrl}/change-password`, {
        currentPassword: currentPasswordInput,
        newPassword: newPasswordInput,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpenChangePasswordDialog(false);
      setCurrentPasswordInput('');
      setNewPasswordInput('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      showSnackbar('Password changed successfully', 'success');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to change password';
      showSnackbar(`Change password failed: ${msg}`, 'error');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <AppBar position="static" sx={{ width: '100%' }}>
          <Toolbar>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src={process.env.PUBLIC_URL + '/ImpleoLogo.png'}
              alt="Impleo"
              sx={{ height: 28, mr: 1, display: { xs: 'none', sm: 'block' } }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>🕓</Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>🕓 Impleo Hours Tracker</Box>
              </Typography>
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Add/Delete project">
                  <IconButton
                    color="inherit"
                    onClick={() => setShowProjectCards((prev) => !prev)}
                    aria-label="toggle-project-cards"
                    size='large'
                    sx={(theme) => ({
                      color: showProjectCards ? theme.palette.warning.light : 'inherit',
                      backgroundColor: showProjectCards ? 'rgba(255,255,255,0.16)' : 'transparent',
                      transition: 'background-color 0.2s ease, color 0.2s ease',
                      '&:hover': {
                        backgroundColor: showProjectCards ? 'rgba(255,255,255,0.26)' : 'rgba(255,255,255,0.12)',
                      },
                    })}
                  >
                    <FormatListBulletedAddIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {!isPwaInstalled && (
              <Tooltip
                title={canInstallPwa ? 'Install application' : 'Install application (unavailable yet)'}
                disableInteractive
              >
                <span>
                  <IconButton
                    color="inherit"
                    onClick={handleInstallApplication}
                    aria-label="install-application"
                    disabled={!canInstallPwa || isInstallingPwa}
                    sx={{ mr: 1 }}
                  >
                    <InstallDesktopIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {isLoggedIn && loggedInRole === 'admin' && (
              <Chip label="Admin" color="warning" size="small" sx={{ mr: 2 }} />
            )}
            {isLoggedIn && loggedInUsername && (
              <Typography variant="body1" component="div" sx={{ mr: 2 }}>
                {loggedInUsername}
              </Typography>
            )}
            {isLoggedIn && (
              <Tooltip title="Change password">
                <IconButton color="inherit" onClick={() => setOpenChangePasswordDialog(true)} aria-label="change-password" sx={{ ml: 1 }}>
                  <VpnKeyIcon />
                </IconButton>
              </Tooltip>
            )}
            {isLoggedIn ? (
              <Tooltip title="Logout">
                <IconButton color="inherit" onClick={logout} aria-label="logout" sx={{ ml: 1 }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Login">
                <IconButton color="inherit" onClick={handleOpenLoginDialog} aria-label="login" sx={{ ml: 1 }}>
                  <LoginIcon />
                </IconButton>
              </Tooltip>
            )}
          </Toolbar>
        </AppBar>

        {/* {notificationSupported && (
          <Box sx={{ width: '100%', maxWidth: 1200, mt: 2, px: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack
                  spacing={2}
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Offline ready & background alerts
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Install this page as an app and enable push notifications to get updates even when the tab
                      is closed.
                    </Typography>
                    {notificationPermission === 'denied' && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        Notifications are blocked by your browser. Allow them in your settings to receive updates.
                      </Typography>
                    )}
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                      variant={isNotificationReady ? 'outlined' : 'contained'}
                      color={isNotificationReady ? 'success' : 'primary'}
                      startIcon={isNotificationReady ? <NotificationsActiveIcon /> : <NotificationsOffIcon />}
                      onClick={handleEnableNotifications}
                      disabled={
                        isNotificationBusy || isNotificationReady || notificationPermission === 'denied'
                      }
                    >
                      {isNotificationReady ? 'Notifications Enabled' : 'Enable Notifications'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<NotificationsActiveIcon />}
                      onClick={handleSendTestNotification}
                      disabled={isNotificationBusy || !isNotificationReady || !isLoggedIn}
                    >
                      Send Test
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )} */}

        {/* Login Dialog */}
        <Dialog open={openLoginDialog} onClose={handleCloseLoginDialog} fullWidth maxWidth="xs">
          <DialogTitle>🔐 Impleo Hours Tracker Login</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                autoFocus
                label="Username"
                type="text"
                fullWidth
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <TextField
                label="Password"
                type={showLoginPassword ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowLoginPassword((v) => !v)}
                        edge="end"
                      >
                        {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLoginDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleLogin}>Login</Button>
            {allowSelfRegistration && (
              <Button onClick={() => { setOpenLoginDialog(false); setOpenRegisterDialog(true); }}>Register</Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Register Dialog */}
        <Dialog open={openRegisterDialog} onClose={handleCloseRegisterDialog} fullWidth maxWidth="xs">
          <DialogTitle>✍️ Create Impleo Hours Tracker Account</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                autoFocus
                label="Username"
                type="text"
                fullWidth
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <TextField
                label="Password"
                type={showRegisterPassword ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowRegisterPassword((v) => !v)}
                        edge="end"
                      >
                        {showRegisterPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRegisterDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleRegister}>Create Account</Button>
            <Button onClick={() => { setOpenRegisterDialog(false); setOpenLoginDialog(true); }}>Back to Login</Button>
          </DialogActions>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={openChangePasswordDialog} onClose={() => setOpenChangePasswordDialog(false)} fullWidth maxWidth="xs">
          <DialogTitle>🔑 Change Password</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Current Password"
                type={showCurrentPassword ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                value={currentPasswordInput}
                onChange={(e) => setCurrentPasswordInput(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle current password visibility"
                        onClick={() => setShowCurrentPassword((v) => !v)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                value={newPasswordInput}
                onChange={(e) => setNewPasswordInput(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNewPassword((v) => !v)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenChangePasswordDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleChangePassword} disabled={!currentPasswordInput || !newPasswordInput}>Update</Button>
          </DialogActions>
        </Dialog>

        {/* Main page (default) or Public project page */}
        {(!publicProjectName && !publicProjectId && !publicProjectPublicId && isLoggedIn) && (
          <Box sx={{ p: 1, width: '100%', maxWidth: 1200 }}>
            <Grid container spacing={2} justifyContent="center">
              {showProjectCards && (
                <>
                  <Grid item xs={12} md={6} lg={4} key={'add-new-project'}>
                    <Card
                      sx={{
                        flexGrow: 1,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          🗓️ Add New Project
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 'auto' }}>
                          <TextField
                            label="Project Name"
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                            variant="outlined"
                            fullWidth
                            size="small"
                            margin="dense"
                          />

                          <IconButton
                            size="large"
                            edge="start"
                            color="inherit"
                            sx={{ ml: 1 }}
                            onClick={createProject}
                          >
                            <Tooltip title="Create project">
                              <AddCircleOutlineIcon />
                            </Tooltip>
                          </IconButton>

                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4} key={'delete-project'}>
                    {projects.length > 0 && (
                      <Card
                        sx={{
                          flexGrow: 1,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            🗑️ Delete Project
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 'auto' }}>
                            <FormControl sx={{ minWidth: 150, flexGrow: 1 }} size="small">
                              <InputLabel id="delete-project-label">Project</InputLabel>
                              <Select
                                labelId="delete-project-label"
                                label="Select Project"
                                value={deleteProjectId}
                                onChange={(e) => setDeleteProjectId(e.target.value)}
                                size="small"
                              >
                                {projects.map((proj) => (
                                  <MenuItem key={proj.id} value={proj.id}>
                                    {proj.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <IconButton
                              size="large"
                              edge="start"
                              color="error"
                              sx={{ ml: 1 }}
                              onClick={deleteSelectedProject}
                            >
                              <Tooltip title="Delete project">
                                <DeleteOutlineIcon />
                              </Tooltip>
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </>
              )}
              {loggedInRole === 'admin' && (
                <Grid item xs={12} md={6} lg={4} key={'users-admin'}>
                  <Card sx={{ flexGrow: 1, width: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        👥 Users
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <IconButton
                          size="large"
                          edge="start"
                          color="inherit"
                          sx={{ ml: 1 }}
                          onClick={() => setUsersDialogOpen(true)}
                        >
                          <Tooltip title="Manage users">
                            <PeopleAltIcon />
                          </Tooltip>
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            <Grid container spacing={2} sx={{ marginTop: 2 }} justifyContent="center">
              {projects.map((proj) => {
                const totalMs = getDisplayTime(proj);
                const timeDisplay = `${formatDDHHMMSS(totalMs, proj.isActive)} (${(totalMs / 3600000).toFixed(2)} hours) `;
                const totalHours = totalMs / 3600000;
                return (
                  <Grid item xs={12} md={6} lg={4} key={proj.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="h6" color={blue[500]} sx={{ fontWeight: 'bold' }}>{proj.name}</Typography>
                          <Box>
                            {proj.isActive ? (
                              <Typography variant="button" color={green[500]} sx={{ fontWeight: 'bold', marginRight: 2 }} >
                                🔴 Active
                              </Typography>
                            ) : (
                              <Typography variant="button" color="grey" sx={{ fontWeight: 'bold', marginRight: 2 }} >
                                Inactive
                              </Typography>
                            )}
                            {proj.isActive ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => stopProject(proj.id)}
                                sx={{ ml: 1 }}
                              >
                                Stop
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => startProject(proj.id)}
                                sx={{ ml: 1 }}
                              >
                                Start
                              </Button>
                            )}
                          </Box>
                        </Box>

                        {editingDescriptionProjectId === proj.id ? (
                          <Box
                            sx={{
                              mt: 2,
                              display: 'grid',
                              gridTemplateColumns: 'auto 1fr',
                              columnGap: 2,
                              rowGap: 1,
                              alignItems: 'start',
                            }}
                          >
                            <Typography sx={{ fontWeight: 500 }}>Description:</Typography>
                            <Stack spacing={1}>
                              <TextField
                                value={descriptionInput}
                                onChange={(e) => setDescriptionInput(e.target.value)}
                                multiline
                                minRows={2}
                                fullWidth
                                size="small"
                              />
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => saveDescription(proj.id)}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={cancelEditDescription}
                                >
                                  Cancel
                                </Button>
                              </Stack>
                            </Stack>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              mt: 2,
                              display: 'grid',
                              gridTemplateColumns: 'auto 1fr auto',
                              columnGap: 2,
                              alignItems: 'start',
                            }}
                          >
                            <Tooltip title="Project description">
                                <Typography sx={{ fontWeight: 500 }}>📝</Typography>
                            </Tooltip>                           
                            <Typography sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                              {proj.description ? proj.description : '—'}
                            </Typography>
                            <IconButton
                              size="small"
                              edge="start"
                              color="inherit"
                              onClick={() => startEditDescription(proj)}
                            >
                              <Tooltip title="Edit description">
                                <EditIcon fontSize="small" />
                              </Tooltip>
                            </IconButton>
                          </Box>
                        )}

                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography sx={{ mt: 2 }}>Total Hours:</Typography>
                          <Typography sx={{ mt: 2, color: proj.isActive ? green[500] : 'inherit' }}>{timeDisplay}</Typography>
                        </Stack>

                        {/* Budget and Balance Row */}
                        {editingBudgetProjectId === proj.id ? (
                          <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
                            <TextField
                              label="Budget (hours)"
                              type="number"
                              value={budgetInput}
                              onChange={(e) => setBudgetInput(e.target.value)}
                              size="small"
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => saveBudget(proj.id)}
                            >
                              Save
                            </Button>
                            <Button
                              variant="text"
                              size="small"
                              onClick={cancelEditBudget}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        ) : (
                          <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
                            <Typography>Budget:</Typography>
                            <Typography>
                              {proj.budget === null || proj.budget === undefined
                                ? '—'
                                : `${Number(proj.budget).toFixed(2)}`}
                            </Typography>
                            <IconButton
                              size="small"
                              edge="start"
                              color="inherit"
                              onClick={() => startEditBudget(proj)}
                              sx={{ ml: 1 }}
                            >
                              <Tooltip title="Edit budget">
                                <EditIcon fontSize="small" />
                              </Tooltip>
                            </IconButton>
                            {proj.budget !== null && proj.budget !== undefined && (
                              <Typography
                                sx={{
                                  ml: 2,
                                  color:
                                    proj.budget - totalHours < 0 ? 'red' : 'inherit',
                                }}
                              >
                                Balance: {(proj.budget - totalHours).toFixed(2)}
                              </Typography>
                            )}
                          </Stack>
                        )}

                        <TextField
                          label="Activity"
                          value={projectActivities[proj.id] || ''}
                          onChange={(e) => {
                            setProjectActivities({
                              ...projectActivities,
                              [proj.id]: e.target.value,
                            });
                          }}
                          onBlur={() =>
                            updateActivity(proj.id, projectActivities[proj.id])
                          }
                          variant="outlined"
                          fullWidth
                          margin="dense"
                          size="small"
                          disabled={proj.isActive}
                        />
                        <IconButton
                          size="small"
                          edge="start"
                          color="inherit"
                          onClick={() => handleAddLogClick(proj.id)}
                        >
                          <Tooltip title="Manually add time">
                            <MoreTimeIcon />
                          </Tooltip>
                        </IconButton>
                        <IconButton
                          size="small"
                          edge="start"
                          color="inherit"
                          sx={{ ml: 2 }}
                          onClick={() => handleUpdateLastEndTimeClick(proj.id)}
                        >
                          <Tooltip title="Update last time">
                            <EditIcon />
                          </Tooltip>
                        </IconButton>
                        <IconButton
                          size="small"
                          edge="start"
                          color="inherit"
                          sx={{ ml: 2 }}
                          onClick={() => handleAddDurationClick(proj.id)}
                        >
                          <Tooltip title="Add duration log">
                            <AddTaskIcon />
                          </Tooltip>
                        </IconButton>
                        <IconButton
                          size="small"
                          edge="start"
                          color="inherit"
                          sx={{ ml: 2 }}
                          onClick={() => handleShowTableClick(proj.id)}
                        >
                          <Tooltip title="Show table">
                            <FormatListNumberedIcon />
                          </Tooltip>
                        </IconButton>


                        {showAddLogProjectId === proj.id && (
                          <Box sx={{ mt: 2, border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Manually Add Log (Start / End)
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <DateTimePicker
                                label="Start Time"
                                value={manualStartDate}
                                onChange={(newValue) => setManualStartDate(newValue)}
                                renderInput={(params) => (
                                  <TextField {...params} fullWidth />
                                )}
                              />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <DateTimePicker
                                label="End Time"
                                value={manualEndDate}
                                onChange={(newValue) => setManualEndDate(newValue)}
                                renderInput={(params) => (
                                  <TextField {...params} fullWidth />
                                )}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Button
                                variant="contained"
                                onClick={() => submitManualLog(proj.id)}
                              >
                                Save
                              </Button>
                            </Box>
                          </Box>
                        )}
                        {showAddDurationProjectId === proj.id && (
                          <Box sx={{ mt: 2, border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Manually Add Log (Duration Only)
                            </Typography>
                            <TextField
                              label="Duration (hours)"
                              value={manualDurationInput}
                              onChange={(e) => setManualDurationInput(e.target.value)}
                              placeholder="e.g. 1.5"
                              type="text"
                              fullWidth
                              sx={{ mb: 2 }}
                              inputProps={{ inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]*' }}
                            />
                            <Stack direction="row" spacing={2} justifyContent="center">
                              <Button variant="contained" onClick={() => submitManualDurationLog(proj.id)}>
                                Save
                              </Button>
                              <Button variant="text" onClick={cancelManualDuration}>
                                Cancel
                              </Button>
                            </Stack>
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          edge="start"
                          color="inherit"
                          sx={{ ml: 2 }}
                          onClick={() => exportLogs(proj)}
                        >
                          <Tooltip title="Export Logs">
                            <DownloadIcon />
                          </Tooltip>
                        </IconButton>

                        <IconButton
                          size="small"
                          edge="start"
                          color="inherit"
                          sx={{ ml: 2 }}
                          onClick={() => importLogs(proj.id)}
                        >
                          <Tooltip title="Import Logs">
                            <UploadIcon />
                          </Tooltip>
                        </IconButton>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {(publicProjectName || publicProjectId || publicProjectPublicId) && (
          <Box sx={{ p: 2, width: '100%', maxWidth: 1200 }}>
            <Grid container spacing={2} justifyContent="center">
              {showProjectCards && (
                <>
                  <Grid item xs={12} md={6} lg={4} key={'add-new-project'}>
                    <Card sx={{ flexGrow: 1, width: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          🗓️ Add New Project
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <TextField
                            label="Project Name"
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                            variant="outlined"
                            fullWidth
                          />
                          <IconButton size="lage" edge="start" color="inherit" sx={{ ml: 1 }} onClick={createProject}>
                            <Tooltip title="Create project">
                              <AddCircleOutlineIcon />
                            </Tooltip>
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4} key={'delete-project'}>
                    {projects.length > 0 && (
                      <Card sx={{ flexGrow: 1, width: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            🗑️ Delete Project
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <FormControl sx={{ minWidth: 150, flexGrow: 1 }}>
                              <InputLabel id="delete-project-label">Project</InputLabel>
                              <Select labelId="delete-project-label" label="Select Project" value={deleteProjectId} onChange={(e) => setDeleteProjectId(e.target.value)}>
                                {projects.map((proj) => (
                                  <MenuItem key={proj.id} value={proj.id}>
                                    {proj.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <IconButton size="lage" edge="start" color="error" sx={{ ml: 1 }} onClick={deleteSelectedProject}>
                              <Tooltip title="Delete project">
                                <DeleteOutlineIcon />
                              </Tooltip>
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </>
              )}
            </Grid>

            <Grid container spacing={2} sx={{ marginTop: 2 }} justifyContent="center">
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <ProjectLogsTable
                      logs={publicProjectLogs}
                      budget={publicProject?.budget}
                      projectName={publicProject?.name || publicProjectName}
                      projectDescription={publicProject?.description}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      <ImportLogsDialog
        open={isImporting}
        onClose={closeImportDialog}
        projectId={importProjectId}
        serverUrl={serverUrl}
        token={token}
        fetchProjects={fetchProjects}
        onMessage={handleMessage}
        onError={handleError}
      />
      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={() => {
          setIsConfirmDialogOpen(false);
          handleDeleteProject();
        }}
        title="Confirm Deletion"
        message="Are you sure you want to delete this project? This action cannot be undone."
      />
      <ProjectLogsTableDialog
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        logs={projectLogs}
        budget={selectedProjectForLogs?.budget}
        projectName={selectedProjectForLogs?.name}
        projectDescription={selectedProjectForLogs?.description}
        projectId={selectedProjectForLogs?.id}
        publicId={selectedProjectForLogs?.publicId}
        token={token}
        serverUrl={serverUrl}
        onLogsUpdated={setProjectLogs}
        onMessage={handleMessage}
        onError={handleError}
        refreshProjects={fetchProjects}
      />
      {loggedInRole === 'admin' && (
        <UsersTableDialog
          open={usersDialogOpen}
          onClose={() => setUsersDialogOpen(false)}
          serverUrl={serverUrl}
          token={token}
          onMessage={handleMessage}
          onError={handleError}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
}

export default App;
